import type { AttributionSnapshot, AttributionTouch } from './wc-funnel';
import {
  ACCOUNT_TERMS_CONSENT_TEXT,
  EMAIL_UPDATES_CONSENT_TEXT,
  OFFICIAL_RULES_VERSION,
  PRIVACY_POLICY_VERSION,
  SMS_CONSENT_TEXT,
  TERMS_VERSION,
} from './privacy-consent';

const TRACKING_PARAMS = [
  'source',
  'campaign',
  'medium',
  'content',
  'term',
  'utm_source',
  'utm_campaign',
  'utm_medium',
  'utm_content',
  'utm_term',
  'ref',
] as const;

export interface SignupResult {
  confirmationRequired: boolean;
}

export interface SignupCaptureMetadata {
  phone?: string;
  favoriteTeamId?: string;
  favoriteTeamName?: string;
  favoriteTeamCode?: string;
  wcUpdatesOptIn?: boolean;
  smsOptIn?: boolean;
  ageConfirmed?: boolean;
  consentCapturedAt?: string;
  consentSourceUrl?: string;
}

function safeRedirectPath(path?: string | null): string {
  if (!path || !path.startsWith('/') || path.startsWith('//')) return '/dashboard';
  return path;
}

function bestTouch(attribution?: AttributionSnapshot | null): AttributionTouch | null {
  return attribution?.latestTouch || attribution?.firstTouch || null;
}

export function buildAuthLink(
  pathname: string,
  searchParams: URLSearchParams,
  fallbackRedirect?: string
): string {
  const next = new URLSearchParams();
  const redirect = searchParams.get('redirect') || fallbackRedirect;
  if (redirect) next.set('redirect', safeRedirectPath(redirect));

  for (const param of TRACKING_PARAMS) {
    const value = searchParams.get(param);
    if (value) next.set(param, value);
  }

  const query = next.toString();
  return query ? `${pathname}?${query}` : pathname;
}

export function buildSignupRedirectUrl(
  origin: string,
  redirectTo: string | undefined,
  attribution?: AttributionSnapshot | null
): string {
  const url = new URL('/login', origin);
  url.searchParams.set('redirect', safeRedirectPath(redirectTo));

  const touch = bestTouch(attribution);
  if (touch?.source) url.searchParams.set('source', touch.source);
  if (touch?.campaign) url.searchParams.set('campaign', touch.campaign);
  if (touch?.medium) url.searchParams.set('medium', touch.medium);
  if (touch?.content) url.searchParams.set('content', touch.content);
  if (touch?.term) url.searchParams.set('term', touch.term);

  return url.toString();
}

export function buildSignupMetadata(
  displayName: string | undefined,
  attribution?: AttributionSnapshot | null,
  capture?: SignupCaptureMetadata
): Record<string, string | undefined> {
  const first = attribution?.firstTouch;
  const latest = attribution?.latestTouch;
  const touch = bestTouch(attribution);

  return {
    display_name: displayName,
    signup_source: touch?.source,
    signup_campaign: touch?.campaign ?? undefined,
    first_touch_source: first?.source,
    first_touch_campaign: first?.campaign ?? undefined,
    latest_touch_source: latest?.source,
    latest_touch_campaign: latest?.campaign ?? undefined,
    latest_touch_url: latest?.url ?? undefined,
    phone: capture?.phone || undefined,
    favorite_team_id: capture?.favoriteTeamId || undefined,
    favorite_team_name: capture?.favoriteTeamName || undefined,
    favorite_team_code: capture?.favoriteTeamCode || undefined,
    wc_updates_opt_in:
      capture?.wcUpdatesOptIn === undefined ? undefined : String(capture.wcUpdatesOptIn),
    sms_opt_in: capture?.smsOptIn === undefined ? undefined : String(capture.smsOptIn),
    age_confirmed: capture?.ageConfirmed === undefined ? undefined : String(capture.ageConfirmed),
    privacy_policy_version: PRIVACY_POLICY_VERSION,
    terms_version: TERMS_VERSION,
    official_rules_version: OFFICIAL_RULES_VERSION,
    consent_captured_at: capture?.consentCapturedAt,
    signup_consent_url: capture?.consentSourceUrl,
    account_terms_consent_text: ACCOUNT_TERMS_CONSENT_TEXT,
    email_updates_consent_text: EMAIL_UPDATES_CONSENT_TEXT,
    sms_consent_text: SMS_CONSENT_TEXT,
  };
}
